import React, { useEffect, useState } from 'react';
import QRCode from 'qrcode.react';

import ExitToAppIcon from '@material-ui/icons/ExitToApp';
import FormControl from '@material-ui/core/FormControl';
import IconButton from '@material-ui/core/IconButton';
import ImageList from '@material-ui/core/ImageList';
import ImageListItem from '@material-ui/core/ImageListItem';
import MenuItem from '@material-ui/core/MenuItem';
import RefreshIcon from '@material-ui/icons/Refresh';
import { makeStyles } from '@material-ui/core/styles';
import Select from '@material-ui/core/Select';
import Typography from '@material-ui/core/Typography';

import * as SingleUseCodeService from '../../api/singleUseCodeService';
import * as StampCampaignService from '../../api/stampCampaignService';

import FullScreenDialog, { FullScreenDialogProps } from '../FullScreenDialog';
import useCompanyContext from '../../hooks/useCompanyContext';
import SelectOtherNumberDialog from './SelectOtherNumberDialog';

const useStampStyles = makeStyles((theme) => ({
  stampContainer: ({ highlight }: { highlight: boolean }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 2,
    borderStyle: 'solid',
    borderColor: theme.palette.grey[400],
    backgroundColor: highlight
      ? theme.palette.primary.main
      : theme.palette.common.white,
    width: 60,
    height: 60,
    borderRadius: 5,
  }),
  stampNumber: ({ highlight }: { highlight: boolean }) => ({
    color: highlight ? theme.palette.common.white : theme.palette.grey[400],
    fontWeight: theme.typography.fontWeightMedium,
  }),
}));

const useStyles = makeStyles((theme) => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    marginLeft: theme.spacing(6),
  },
  logoContainer: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
  },
  merchantLogo: {
    height: 120,
    width: 120,
    borderRadius: '50%',
    objectFit: 'cover',
    boxShadow: '0 2px 10px rgba(199, 199, 199, 0.5)',
  },
  header: {
    marginTop: theme.spacing(2),
    textAlign: 'center',
    width: 350,
  },
  mainSubtextContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    width: 350,
  },
  mainSubtextExpiredContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: 40,
    width: 350,
  },
  subtext: {
    textAlign: 'center',
  },
  qr: {
    margin: theme.spacing(),
  },
  box: {
    flex: 1,
    display: 'flex',
    justifyContent: 'center',
  },
  close: {
    marginLeft: 'auto',
  },
  closeIcon: {
    color: theme.palette.common.black,
  },
  refresh: {
    height: 60,
    width: 60,
  },
  tile: {
    display: 'flex',
    justifyContent: 'center',
  },
  root: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    flexWrap: 'wrap',
    width: 350,
    marginTop: theme.spacing(),
  },
  qrContainer: {
    marginTop: theme.spacing(17),
    marginBottom: theme.spacing(17),
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
  },
  expired: {
    position: 'absolute',
    color: theme.palette.primary.main,
    fontSize: 24,
    textAlign: 'center',
    zIndex: 2,
  },
  formControl: {
    minWidth: 250,
  },
  selectEmpty: {
    marginTop: theme.spacing(2),
  },
}));

function Stamp({
  value,
  highlight,
  handleClick,
}: {
  value: string;
  highlight: boolean;
  handleClick: () => void;
}) {
  const classes = useStampStyles({ highlight });

  return (
    <div className={classes.stampContainer} onClick={handleClick}>
      <Typography variant="h4" className={classes.stampNumber}>
        {value}
      </Typography>
    </div>
  );
}

type Props = Pick<FullScreenDialogProps, 'open'> & {
  onClose: () => void;
  stampNum: string;
  storeId: string;
  onlyScanOnce?: boolean;
  maxSelectedNumber: number;
  stampCampaignList: StampCampaignService.StampCampaign[];
};

export default function QRCodeModeDialog({
  open,
  onClose,
  stampNum,
  storeId,
  onlyScanOnce = false,
  maxSelectedNumber,
  stampCampaignList,
}: Props) {
  const INIT_COUNTDOWN = 90;
  const maxStampStringNumbers: string[] = Array.from(
    { length: maxSelectedNumber },
    (_, index) => (index + 1).toString()
  );
  const initStampArray: string[] = ['1', '2', '3', '4', '5', '6', '7', '+'];
  const classes = useStyles();
  const { company } = useCompanyContext();
  const [deeplink, setDeeplink] = useState('');
  const [countdown, setCountdown] = useState(-1);

  const [selectedStamp, setSelectedStamp] = useState(stampNum);
  const [selectOtherNumberOpenDialog, setSelectOtherNumberOpenDialog] =
    useState(false);
  const [stampArray, setStampArray] = useState(initStampArray);
  const [selectedSlotNumber, setSelectedSlotNumber] = useState(
    stampCampaignList.length === 1 ? stampCampaignList[0].slotNumber : 0
  );
  const [selectOpen, setSelectOpen] = useState(false);

  useEffect(() => {
    const fetch = async () => {
      if (company && selectedSlotNumber > 0) {
        try {
          const res = await SingleUseCodeService.generate({
            companyId: company.id,
            numOfStamps: parseInt(selectedStamp, 10),
            slotNumber: selectedSlotNumber,
            storeId,
          });
          setDeeplink(res.deepLink);
          setCountdown(INIT_COUNTDOWN);
        } catch (e: any) {
          console.log('Error: ', e);
        }
      }
    };
    if (selectedSlotNumber > 0) {
      fetch();
    }
  }, [selectedStamp, company, storeId, selectedSlotNumber]);

  useEffect(() => {
    const countdownTimer = setTimeout(() => {
      if (countdown > 0) {
        setCountdown(countdown - 1);
      }
    }, 1000);

    return () => {
      clearTimeout(countdownTimer);
    };
  }, [countdown]);

  const handleSelectStampCampaign = (
    event: React.ChangeEvent<{ value: unknown }>
  ) => {
    setSelectedSlotNumber(event.target.value as number);
    setSelectOpen(!selectOpen);
  };

  const generateNewQR = async (stampNum: number) => {
    if (company && selectedSlotNumber > 0) {
      try {
        const res = await SingleUseCodeService.generate({
          companyId: company.id,
          numOfStamps: stampNum,
          slotNumber: selectedSlotNumber,
          storeId,
        });
        setDeeplink(res.deepLink);
        setCountdown(INIT_COUNTDOWN);
      } catch (e: any) {
        console.log('Error: ', e);
      }
    }
  };

  const renderStamps = () => {
    let arrayStamp: string[] = [];

    if (maxSelectedNumber > 8) {
      arrayStamp = stampArray;
    } else {
      arrayStamp = maxStampStringNumbers;
    }

    return arrayStamp.map((stmp, idx) => (
      <ImageListItem key={idx} cols={1} rows={1} className={classes.tile}>
        <Stamp
          value={stmp}
          highlight={stmp === selectedStamp}
          handleClick={() => {
            if (stmp === '+') {
              setSelectOtherNumberOpenDialog(true);
            } else {
              setCountdown(0);
              setSelectOtherNumberOpenDialog(false);
              setSelectedStamp(stmp);
              generateNewQR(parseInt(stmp, 10));
              if (maxSelectedNumber > 8) {
                setStampArray(initStampArray);
              }
            }
          }}
        />
      </ImageListItem>
    ));
  };
  const onRefresh = (stampNum: number) => {
    setCountdown(0);
    generateNewQR(stampNum);
  };

  if (!company) {
    return null;
  }

  return (
    <FullScreenDialog open={open}>
      <div className={classes.box}>
        <div className={classes.close}>
          <IconButton
            onClick={() => {
              setDeeplink('');
              onClose();
            }}
          >
            <ExitToAppIcon className={classes.closeIcon} />
          </IconButton>
        </div>
      </div>
      <div className={classes.logoContainer}>
        <img
          src={company.logo?.versions.xl}
          className={classes.merchantLogo}
          alt="flex reward"
        />
        {stampCampaignList.length > 1 && (
          <FormControl className={classes.formControl}>
            <Select
              value={selectedSlotNumber}
              onChange={handleSelectStampCampaign}
              className={classes.selectEmpty}
              inputProps={{ 'aria-label': 'selectedSlotNumber' }}
              onOpen={() => setSelectOpen(!selectOpen)}
              displayEmpty
            >
              {selectedSlotNumber === 0 && !selectOpen && (
                <MenuItem value={0}>{'Select Card'}</MenuItem>
              )}
              {stampCampaignList.map((s) => (
                <MenuItem value={s.slotNumber}>{s.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
        {selectedSlotNumber > 0 && (
          <>
            <Typography variant="h4" className={classes.header}>
              SCAN FOR {selectedStamp} STAMP
              {parseInt(selectedStamp, 10) > 1 ? 'S' : ''}
            </Typography>
            <Typography variant="body1" className={classes.header}>
              {countdown.toString()} second{countdown > 1 ? 's' : ''} remaining
            </Typography>
            <div className={classes.qr}>
              {countdown === 0 ? (
                <div className={classes.qrContainer}>
                  <QRCode
                    value={deeplink}
                    size={350}
                    renderAs="canvas"
                    includeMargin
                    fgColor={'#e1e1e1'}
                    style={{ zIndex: 1, position: 'absolute' }}
                  />
                  <Typography className={classes.expired}>Expired</Typography>
                </div>
              ) : (
                <QRCode
                  value={deeplink}
                  size={350}
                  renderAs="svg"
                  includeMargin
                />
              )}
            </div>
            {onlyScanOnce && (
              <>
                <div
                  className={
                    countdown === 0
                      ? classes.mainSubtextExpiredContainer
                      : classes.mainSubtextContainer
                  }
                >
                  <Typography variant="body1" className={classes.subtext}>
                    {
                      'Each code can only be scanned once. Refresh for new code.'
                    }
                  </Typography>
                </div>
              </>
            )}
            {!onlyScanOnce && (
              <>
                <div
                  className={
                    countdown === 0
                      ? classes.mainSubtextExpiredContainer
                      : classes.mainSubtextContainer
                  }
                >
                  <Typography variant="body1" className={classes.subtext}>
                    {
                      'Each code can only be scanned once. Refresh for new code or select different stamp number.'
                    }
                  </Typography>
                </div>
              </>
            )}
            <div>
              <IconButton
                onClick={() => {
                  onRefresh(parseInt(selectedStamp, 10));
                }}
              >
                <RefreshIcon className={classes.refresh} />
              </IconButton>
            </div>
            {!onlyScanOnce && (
              <div className={classes.root}>
                <ImageList rowHeight={60} cols={4} gap={12}>
                  {renderStamps()}
                </ImageList>
              </div>
            )}
          </>
        )}
      </div>
      <SelectOtherNumberDialog
        open={selectOtherNumberOpenDialog}
        onClose={() => setSelectOtherNumberOpenDialog(false)}
        confirmSelectedNumber={(selectNum: string) => {
          setCountdown(0);
          let updateStampArray = stampArray;
          updateStampArray.pop();
          updateStampArray.push(selectNum);
          setStampArray(updateStampArray);
          setSelectedStamp(selectNum);
          setSelectOtherNumberOpenDialog(false);
          generateNewQR(parseInt(selectNum, 10));
        }}
        maxSelectedNumber={maxSelectedNumber}
      />
    </FullScreenDialog>
  );
}
